// =====================================================================
// Chamados — A JANELA DE MIGRAÇÃO DO AUVO (Julia, 10/09/2026).
//
// ⚠️⚠️ ISTO É TEMPORÁRIO, E FOI FEITO PARA SER APAGADO — mesmo espírito do
//    `chamados-piloto.js`. O módulo abre para a rede inteira na tarde de
//    10/09/2026, e as lojas chegam com chamados JÁ ABERTOS no Auvo, alguns de
//    semanas atrás. Sem um campo de data, migrar esses chamados os faria nascer
//    todos com a data de hoje — e o portal passaria a mentir justamente sobre o
//    número que ele existe para consertar: há quanto tempo a loja espera.
//
//    QUANDO A JANELA FECHAR (depois de 18/09/2026), o campo some sozinho: a data
//    está aqui, não num deploy. Só depois disso vale remover o arquivo, as
//    chamadas e o campo da tela. Fazer o contrário (apagar o arquivo primeiro)
//    quebra o import e derruba a abertura de chamado para a rede inteira.
//
// ---------------------------------------------------------------------
// O QUE ELE DECIDE — e o que NÃO decide
// ---------------------------------------------------------------------
// Ele responde três perguntas e só isso: "a janela ainda está aberta?", "este
// perfil pode retroagir a data?" e "esta data digitada pode virar `ABERTO_EM`?".
// Quem pode abrir chamado continua saindo de `PERFIS_ABERTURA`, e o recorte por
// loja do `escopoLojaSql`.
//
// ⚠️⚠️ RETROAGIR A DATA É DE `Gerente TI` E `Admin`, NÃO DA LOJA (Julia,
//    10/09/2026) — ver `PERFIS_MIGRACAO`. A transcrição do que estava no Auvo é
//    trabalho do TI, que tem a fila de lá na mão; o gerente de loja abre chamado
//    novo e não deve ter na tela um campo capaz de mudar o próprio SLA dele.
//    ⚠️ A conferência de perfil é a `podeInformarAbertura`, e ela é SEPARADA da
//    `resolverAberturaInformada` de propósito: uma responde "quem", a outra
//    "quando". Esconder o campo sem a primeira mostraria à loja um campo que o
//    servidor recusa com 403.
//
// ⚠️⚠️ CAMPO EM BRANCO É O CASO NORMAL, não um campo faltando: chamado aberto de
//    verdade nasce AGORA, com o `DEFAULT CURRENT_TIMESTAMP` da coluna. A data só
//    é informada por quem está transcrevendo um chamado que já existia no Auvo.
//
// ⚠️⚠️ CAMPO PREENCHIDO CARIMBA `ORIGEM = 'Auvo'`, e é isso que impede o portal
//    de reescrever a própria história em silêncio: a `dtbChamado` NÃO tem
//    `CRIADO_EM`, então sem o carimbo um chamado com `ABERTO_EM` de cinco dias
//    atrás fica indistinguível de um que a loja realmente abriu naquele dia. O
//    painel de SLA vai cobrar prazo dele (e mostrá-lo vencido, o que é honesto —
//    o chamado É velho), e a `ORIGEM` é a única resposta possível para "por que
//    este aqui nasceu estourado?".
//
// ⚠️ FORA DA JANELA, DATA INFORMADA É RECUSADA — nunca ignorada em silêncio. A
//    tela esconde o campo a partir de 19/09, então quem manda data depois disso
//    é uma aba velha, aberta desde a véspera. Gravar "agora" calado deixaria a
//    pessoa convencida de que retroagiu o chamado; o 400 manda recarregar e
//    resolve o caso na hora.
// =====================================================================

/**
 * O último dia em que a data de abertura pode ser informada — INCLUSIVE.
 * A janela vale o dia 18/09/2026 inteiro e fecha na virada para o dia 19.
 *
 * ⚠️⚠️ ESTA CONSTANTE TEM UM ESPELHO NO BACK
 *    (`server/chamados/chamados-migracao-auvo.js`), porque esta tela precisa
 *    decidir se mostra o campo sem perguntar à API. Divergir dá os dois sintomas
 *    ruins: campo na tela que o servidor recusa com 400, ou campo escondido
 *    enquanto o servidor ainda o aceitaria. Há teste travando
 *    (`server/tests/espelhos-back-front.test.js`).
 */
export const FIM_JANELA = '2026-09-18';

/**
 * O chão da data aceita. NÃO é regra de negócio — é caça-erro de digitação.
 *
 * ⚠️ Chamado velho do Auvo é o normal (63% da fila de Manutenção de lá nunca saiu
 *    do primeiro estado), então o chão é generoso de propósito. O que ele pega é
 *    o ano errado: '2016-09-05' no lugar de '2026-09-05'.
 */
export const INICIO_PERMITIDO = '2025-01-01';

/**
 * A hora gravada quando só a data é informada — 08:00, início do expediente.
 *
 * ⚠️ O campo da tela é `type="date"` (decisão da Julia, 10/09): a loja sabe o DIA
 *    em que abriu no Auvo, quase nunca a hora, e pedir hora de pé na loja é
 *    atrito por um dado que viria chutado. 08:00 e não 00:00 porque meia-noite é
 *    hora de loja fechada — num relatório, "aberto às 00:00" se lê como dado
 *    sintético, que é exatamente o que não queremos parecer.
 * ⚠️ A hora só muda o SLA da faixa Alta (18h corridas). Média e Baixa contam em
 *    dias úteis, onde a hora do dia não altera o vencimento.
 */
export const HORA_PADRAO = '08:00:00';

/**
 * Quem pode informar a data de abertura — e portanto quem enxerga o campo.
 *
 * ⚠️⚠️ ESPELHO de `server/chamados/chamados-migracao-auvo.js` › `PERFIS_MIGRACAO`.
 *    Divergir dá os dois sintomas mudos: campo na tela que o envio recusa com
 *    403, ou campo escondido de quem é justamente o dono da migração.
 *
 * ⚠️ `Admin` está escrito aqui e NÃO entra por bypass: a tela não tem bypass.
 * ⚠️ A LOJA FICA DE FORA de propósito — inclusive `Gerente` e
 *    `Gerente Plantao`. Data de abertura mexe no prazo do próprio chamado dela.
 */
export const PERFIS_MIGRACAO = ['Admin', 'Gerente TI'];

/**
 * Este perfil pode retroagir a data de abertura?
 *
 * @param {string} perfil  o perfil de quem está abrindo (já traduzido pelo
 *                         `perfilNoModulo(perfil, 'chamados')`)
 * @returns {boolean}
 *
 * ⚠️ Compara em MINÚSCULAS e por elemento EXATO, igual ao `authorizeRoles` do
 *    backend: nunca `startsWith`/`includes` de substring, senão `Gerente TI` e
 *    `Gerente` passariam a se confundir.
 */
export function podeInformarAbertura(perfil) {
    const p = String(perfil ?? '').trim().toLowerCase();
    return PERFIS_MIGRACAO.some((x) => x.toLowerCase() === p);
}

/**
 * 'AAAA-MM-DD' no fuso LOCAL.
 *
 * ⚠️ Nunca `toISOString()`: ele converte para UTC e devolve o dia ANTERIOR em
 *    toda a madrugada brasileira (UTC-3), o que faria a janela fechar um dia
 *    antes para quem trabalha à noite.
 */
export function dataLocalISO(d) {
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
}

/** '2026-09-18' vira '18/09/2026'. Só para as mensagens de erro. */
export function formatarBR(iso) {
    const [ano, mes, dia] = String(iso).split('-');
    return `${dia}/${mes}/${ano}`;
}

/**
 * A janela de migração ainda está aberta?
 *
 * @param {Date} agora  o relógio (injetável, para o teste não medir o dia real)
 * @returns {boolean}
 *
 * ⚠️ Compara STRING 'AAAA-MM-DD', não `Date`: o formato ISO ordena
 *    lexicograficamente, o que dá a comparação por DIA de graça — sem hora, sem
 *    fuso e sem o clássico "vence à meia-noite ou às 23:59?".
 */
export function janelaMigracaoAberta(agora = new Date()) {
    return dataLocalISO(agora) <= FIM_JANELA;
}

/**
 * Traduz o campo de data da tela no par (`ABERTO_EM`, `ORIGEM`) que o `INSERT` usa.
 *
 * @param {string|null} valor  o que veio do `<input type="date">` ('AAAA-MM-DD' ou vazio)
 * @param {Date} agora         o relógio
 * @returns {{abertoEm: string|null, origem: 'Portal'|'Auvo'}}
 *          `abertoEm` nulo = deixe o `CURRENT_TIMESTAMP` da coluna valer.
 * @throws {Error} com a mensagem já PRONTA para virar o corpo do 400.
 *
 * ⚠️⚠️ DEVOLVE STRING, NUNCA UM `Date`. O `mysql2` converte objeto `Date` usando
 *    o fuso do processo Node, que não é necessariamente o do MySQL — e a
 *    diferença apareceria como um chamado gravado no dia anterior, sem erro
 *    nenhum. A string 'AAAA-MM-DD HH:MM:SS' viaja literal e ninguém a converte.
 */
export function resolverAberturaInformada(valor, agora = new Date()) {
    const bruto = String(valor ?? '').trim();

    // O caso normal e majoritário: ninguém informou nada.
    if (!bruto) return { abertoEm: null, origem: 'Portal' };

    if (!janelaMigracaoAberta(agora)) {
        throw new Error(
            `A migração de chamados do Auvo encerrou em ${formatarBR(FIM_JANELA)}. `
            + 'Recarregue a página (Ctrl+Shift+R) e abra o chamado normalmente — '
            + 'ele nasce com a data de hoje.'
        );
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(bruto)) {
        throw new Error('Data de abertura em formato inválido — use o seletor de data do formulário.');
    }

    // ⚠️ `T00:00:00` sem o `Z`: força a leitura no fuso LOCAL. Sem isso o
    //    JavaScript trata 'AAAA-MM-DD' puro como UTC e o dia volta trocado.
    // ⚠️ O ida-e-volta é o que reprova data inexistente (31/02): o `Date` a
    //    "conserta" para 03/03 em silêncio, e só a comparação com a string
    //    original enxerga isso.
    const d = new Date(`${bruto}T00:00:00`);
    if (Number.isNaN(d.getTime()) || dataLocalISO(d) !== bruto) {
        throw new Error(`Essa data não existe no calendário: ${formatarBR(bruto)}.`);
    }

    if (bruto > dataLocalISO(agora)) {
        throw new Error('A data de abertura não pode estar no futuro — o chamado já aconteceu.');
    }
    if (bruto < INICIO_PERMITIDO) {
        throw new Error(
            `Data de abertura anterior a ${formatarBR(INICIO_PERMITIDO)} `
            + `(${formatarBR(bruto)}) — confira o ano.`
        );
    }

    return { abertoEm: `${bruto} ${HORA_PADRAO}`, origem: 'Auvo' };
}
